import { createElement } from "react";
import SvgIcon from "@mui/material/SvgIcon";
import type { Components, Theme } from "@mui/material/styles";
import {
  CaretLeft as LeftCaretIcon,
  CaretRight as RightCaretIcon,
} from "@app/components/icons/uiControlIcons.tsx";

import {
  borderWidth,
  color,
  fontSize,
  fontWeight,
  lineHeight,
  opacity,
  spacing,
  transition,
} from "@app/theme/weaveTokens.ts";

type ThemeComponents = Components<Omit<Theme, "components">>;

/**
 * Colours and motion for one Tab state (DHIG's `tab.*` component tokens, which
 * Weave has no equivalent for: Weave's tabs are an underlined design).
 */
const tabState = (
  backgroundColor: string,
  borderColor: string,
  textColor: string,
  motion: typeof transition.activate
) => ({
  backgroundColor,
  borderColor,
  borderWidth: borderWidth.thick,
  color: textColor,
  ...motion,
});

/** The DHIG tab states, shared with the quiet Tabs look in `quietTabsSx`. */
export const tabStates = {
  default: tabState(
    color.transparent,
    color.transparent,
    color.inkMuted,
    transition.activate
  ),
  active: tabState(
    color.surface,
    color.transparent,
    color.ink,
    transition.blur
  ),
  hover: tabState(
    color.hoverFillSubtle,
    color.transparent,
    color.inkMuted,
    transition.blur
  ),
  focus: tabState(
    color.transparent,
    color.iris,
    color.inkMuted,
    transition.activate
  ),
  pressed: tabState(
    color.pressedFillSubtle,
    color.transparent,
    color.inkMuted,
    transition.activate
  ),
};

/** The "default" Tabs bar: grey fill, no padding or shadow. */
const tabsBar = {
  color: color.inkMuted,
  opacity: opacity.visible,
  padding: spacing.none,
  backgroundColor: color.tabBar,
  boxShadow: "none",
};

/**
 * Restores the Digital HIG "default" Tabs look (filled grey bar, selected tab
 * lifted onto a white fill, indicator on top). DHIG's Tabs wrapper adds the
 * `DhigTabs--styleVariant--default` class, which Weave's theme does not style:
 * Weave's scrollable Tabs get the bottom indicator and divider instead.
 */
export const tabsOverrides: ThemeComponents = {
  MuiTabs: {
    // DHIG's caret icons for the scroll buttons, in place of Weave's arrows.
    defaultProps: {
      slots: {
        StartScrollButtonIcon: () =>
          createElement(
            SvgIcon,
            { fontSize: "small" },
            createElement(LeftCaretIcon, null)
          ),
        EndScrollButtonIcon: () =>
          createElement(
            SvgIcon,
            { fontSize: "small" },
            createElement(RightCaretIcon, null)
          ),
      },
    },
    styleOverrides: {
      root: {
        // Weave already overlays the scroll buttons with a fade like DHIG
        // does, but at 32px wide, with a grey icon colour and a fade that
        // ends in black-transparent instead of white-transparent.
        "& [class*='MuiTabScrollButton-root']": {
          color: color.ink,
          width: spacing.xxl,
          background: `linear-gradient(270deg, ${color.fadeTransparent} 0%, ${color.fadeSemiTransparent} 33.33%, ${color.fadeSemiOpaque} 66.67%, ${color.fadeOpaque} 100%)`,
        },
        "& [class*='MuiTabs-scroller'] + [class*='MuiTabScrollButton-root']": {
          background: `linear-gradient(270deg, ${color.fadeOpaque} 0%, ${color.fadeSemiOpaque} 33.33%, ${color.fadeSemiTransparent} 66.67%, ${color.fadeTransparent} 100%)`,
        },
        "&&.DhigTabs--styleVariant--default": {
          ...tabsBar,
          '& [class*="MuiTabs-scroller"]': {
            marginLeft: spacing.none,
            boxShadow: "none",
            '& [class*="MuiTabs-indicator"]': {
              top: spacing.none,
              bottom: "unset",
              background: color.ink,
              "&:after": { content: "none" },
            },
          },
          '& [class*="MuiTab-root"]': {
            ...tabStates.default,
            fontSize: fontSize.large,
            fontWeight: fontWeight.medium,
            lineHeight: lineHeight.compact,
            padding: `${spacing.m} ${spacing.l}`,
            textTransform: "none",
            "&:hover": { ...tabStates.hover },
            "&:focus-visible": { ...tabStates.focus },
            "&:active": { ...tabStates.pressed },
            "&.Mui-selected": { ...tabStates.active },
            "&.Mui-selected:focus-visible": {
              ...tabStates.focus,
              backgroundColor: color.surfaceRaised,
              color: color.ink,
            },
            "&.Mui-disabled": { opacity: opacity.disabled },
          },
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        border: `${borderWidth.thick} solid transparent`,
        minWidth: "unset",
      },
    },
  },
};
